import axiosInstance from './axiosInstance';
import { normalizeAxiosError } from '@/lib/error';
import {
  DeliveryIntakeForm,
  DeliveryCreateRequest,
  DeliveryListQueryParams,
  EditIntakeFormRequest,
  EditWorkOrderRequest,
  DeliveryUpdateEmailRequest,
  DeliveryListResponse,
  DeliveryUpdateDetail,
  DeliveryUpdateCreateRequest,
  DeliveryUpdateUpdateRequest,
  DeliveryUpdatesResponse
} from '@/types/delivery.types';
import { DeliveryDetailResponse } from '@/types/deliveryDetail.types';
import {
  DeliveryUpdateImage,
  DeliveryUpdateEmailResponse,
} from '@/types/deliveryUpdate.types';
import {
  DeliveryInvoiceDetailResponse,
  DeliveryInvoiceEditRequest,
  DeliveryInvoiceEditResponse,
} from '@/types/deliveryInvoice.types';
import { useQuery, UseQueryOptions, useMutation } from '@tanstack/react-query';
import { downloadFilefromBuffer } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import type { AxiosError } from 'axios';
import { getMediaPresignedUrl } from '@/services/uploadMedia';
import { AdvanceFilter, SortDirection } from '@/types/types';
import type { GenericErrorResponse } from '@/types/api.types';

// Temporary type assertion helper for loose API responses
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyResponse = any;

export interface County {
  countyId: string;
  name: string;
  state?: string;
}

export interface DispatchRate {
  rateId: string;
  pickupCountyId: string;
  deliveryCountyId: string;
  baseCharge: number;
}

export interface User {
  userId: string; // or id
  id?: string;
  firstName: string;
  lastName: string;
  phone?: string;
  status?: string;
  isActive?: boolean;
}

interface UserResponse {
  users?: User[];
  data?: User[];
  [key: string]: unknown;
}

interface SingleUserResponse {
  users?: User;
  [key: string]: unknown;
}

export const createDeliveryIntake = async (
  data: DeliveryCreateRequest
): Promise<{
  data?: DeliveryIntakeForm;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<DeliveryIntakeForm>('/delivery/intake-form', data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

// NOTE: GET /delivery/intake-form/{intakeId} does not exist in new API. 
// Use getDeliveryDetail instead.
export const getDeliveryIntake = (
  intakeId: string
): Promise<{
  data?: DeliveryIntakeForm;
  error?: GenericErrorResponse;
}> => {
  console.warn('getDeliveryIntake is deprecated. Use getDeliveryDetail.', intakeId);
  return Promise.resolve({ error: { error: { code: 'DEPRECATED', message: 'Use getDeliveryDetail' } } as GenericErrorResponse });
};

export const updateDeliveryIntake = async (
  intakeId: string,
  data: EditIntakeFormRequest
): Promise<{
  data?: DeliveryIntakeForm;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.put<DeliveryIntakeForm>(`/delivery/intake-form/${intakeId}`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

/**
 * Fetch list of counties for dropdown
 */
export const getCounties = async (): Promise<{
  data?: County[];
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<any>('/delivery/counties');
    const counties = Array.isArray(response.data) ? response.data : (response.data?.counties || response.data?.data || []);
    return { data: counties };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

/**
 * Get dispatch rate for pickup and delivery counties
 */
export const getDispatchRate = async (
  pickupCountyId: string,
  deliveryCountyId: string
): Promise<{
  data?: DispatchRate;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<DispatchRate>('/delivery/rates', {
      params: {
        pickupCountyId,
        deliveryCountyId,
      },
    });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const searchDgrUsers = async (
  searchTerm: string
): Promise<{
  data?: User[];
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<UserResponse>('/users', {
      params: {
        search: searchTerm,
        isActive: true,
        pageSize: 100,
        sortField: 'userName',
        sortDirection: 'asc',
      },
    });
    // Handle potential response structure variations found in consumer code
    const users = response.data.users || response.data.data || [];
    return { data: users };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const getUserDetails = async (
  userId: string
): Promise<{
  data?: User;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<SingleUserResponse | User>(`/users/${userId}`);
    // Handle potential response structure variations
    const data = response.data;
    const user = 'users' in data ? data.users : (data as User);
    return { data: user };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const getClientDetails = async (
  clientId: string
): Promise<{
  data?: unknown; // Define Client interface if possible
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<unknown>(`/clients/${clientId}`);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const getContactDetails = async (
  contactId: string
): Promise<{
  data?: unknown; // Define Contact interface if possible
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<unknown>(`/contacts/${contactId}`);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

// Delivery List API
const isActiveDeliveryFilter = (
  filter: AdvanceFilter
): filter is AdvanceFilter & { field: string; operator: NonNullable<AdvanceFilter['operator']>; value: string; fieldType: NonNullable<AdvanceFilter['fieldType']> } =>
  Boolean(filter.field && filter.operator && filter.value && filter.fieldType);

/** Maps DataTable query params to the GET /delivery/list contract. */
export const buildDeliveryListApiParams = (params: DeliveryListQueryParams): DeliveryListQueryParams => {
  const { pageNo, pageSize, sortField, sortDirection, filters } = params;
  const activeFilters = (filters ?? []).filter(isActiveDeliveryFilter);
  const result: DeliveryListQueryParams = {};

  if (pageNo !== undefined) {
    result.pageNo = pageNo;
  }
  if (pageSize !== undefined) {
    result.pageSize = pageSize;
  }
  if (sortField && sortDirection) {
    result.sortField = sortField;
    result.sortDirection = sortDirection;
  }
  if (activeFilters.length > 0) {
    result.filters = activeFilters;
  }

  return result;
};

export const getDeliveryList = async (
  params: DeliveryListQueryParams
): Promise<{
  data?: DeliveryListResponse;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<DeliveryListResponse>('/delivery/list', {
      params: buildDeliveryListApiParams(params),
    });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const useDeliveryList = (
  params: DeliveryListQueryParams,
  options?: { query?: UseQueryOptions<DeliveryListResponse> }
) => {
  const apiParams = buildDeliveryListApiParams(params);
  const query = useQuery({
    queryFn: async () => {
      const result = await getDeliveryList(apiParams);
      if (result.error) {
        throw result.error;
      }
      return result.data!;
    },
    queryKey: ['delivery-list', apiParams],
    ...options?.query,
  });

  const data = (query.data as AnyResponse)?.items ?? [];
  const totalRows = (query.data as AnyResponse)?.totalCount ?? 0;
  return { ...query, data, totalRows };
};

// Get delivery detail (Intake or Work Order)
export const getDeliveryDetail = async (
  intakeId: string
): Promise<{
  data?: DeliveryDetailResponse;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<DeliveryDetailResponse>(`/delivery/${intakeId}/detail`);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const useDeliveryDetail = (intakeId: string | null, options?: UseQueryOptions<DeliveryDetailResponse>) => {
  return useQuery({
    queryFn: async () => {
      if (!intakeId) throw new Error('Intake ID is required');
      const result = await getDeliveryDetail(intakeId);
      if (result.error) {
        throw result.error;
      }
      if (!result.data) {
        throw new Error('No data returned from API');
      }
      return result.data;
    },
    queryKey: ['delivery-detail', intakeId],
    enabled: !!intakeId,
    ...options,
  });
};

export const useSingleDeliveryUpdate = (
  workOrderId: string | null,
  updateId: string | null,
  options?: UseQueryOptions<DeliveryUpdateDetail>
) => {
  return useQuery({
    queryFn: async () => {
      if (!workOrderId || !updateId) throw new Error('Work Order ID and Update ID are required');
      const result = await getSingleDeliveryUpdate(workOrderId, updateId);
      if (result.error) {
        throw result.error;
      }
      if (!result.data) {
        throw new Error('No data returned from API');
      }
      return result.data;
    },
    queryKey: ['delivery-update', workOrderId, updateId],
    enabled: !!workOrderId && !!updateId,
    ...options,
  });
};

// Convert intake to work order
export const convertToWorkOrder = async (
  intakeId: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<unknown>('/delivery/convert-to-work-order', { intakeId });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const assignToMe = async (
  workOrderId: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<unknown>('/delivery/assign-to-me', { workOrderId });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const assignIntakeToMe = async (
  intakeId: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    // TODO: Backend API verification needed - Confirm that /delivery/assign-to-me
    // supports both workOrderId and intakeId parameters.
    // Currently only workOrderId is documented in OpenAPI spec.
    // If backend doesn't support intakeId yet, this will return an error.
    const response = await axiosInstance.post<unknown>('/delivery/assign-to-me', { intakeId });
    return { data: response.data };
  } catch (error) {
    return { error: normalizeAxiosError(error) };
  }
};

export const cancelWorkOrder = async (
  workOrderId: string,
  reason: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<unknown>(`/delivery/work-order/${workOrderId}/cancel`, { reason });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const cancelIntakeForm = async (
  intakeId: string,
  reason: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<unknown>(`/delivery/intake-form/${intakeId}/cancel`, { reason });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const updateWorkOrder = async (
  workOrderId: string,
  data: EditWorkOrderRequest
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.put<unknown>(`/delivery/work-order/${workOrderId}`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

// Deprecated: Rescheduling is done via updateWorkOrder
export const rescheduleWorkOrder = (
  workOrderId: string,
  newDeliveryDate: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  console.warn('rescheduleWorkOrder is deprecated. Use updateWorkOrder.', workOrderId, newDeliveryDate);
  return Promise.resolve({ error: { error: { code: 'DEPRECATED', message: 'Use updateWorkOrder' } } as GenericErrorResponse });
};

export const updateWorkOrderStatus = async (
  workOrderId: string,
  status: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.patch<unknown>(`/delivery/work-order/${workOrderId}/status`, { status });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const exportDeliverySheet = async (
  workOrderId: string
): Promise<{
  data?: Blob;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<Blob>(`/delivery/work-order/${workOrderId}/export-delivery-sheet`, {}, {
      responseType: 'blob'
    });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const getWorkOrderComments = async (
  workOrderId: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.get<unknown>(`/delivery/work-order/${workOrderId}/comments`);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

export const addWorkOrderComment = async (
  workOrderId: string,
  comment: string
): Promise<{
  data?: unknown;
  error?: GenericErrorResponse;
}> => {
  try {
    const response = await axiosInstance.post<unknown>(`/delivery/work-order/${workOrderId}/comments`, { comment });
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

// Delivery Updates
export const getDeliveryUpdates = async (workOrderId: string): Promise<{ data?: DeliveryUpdatesResponse; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DeliveryUpdatesResponse>(`/delivery/work-order/${workOrderId}/delivery-updates`);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

export const getSingleDeliveryUpdate = async (
  workOrderId: string,
  updateId: string
): Promise<{ data?: DeliveryUpdateDetail; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DeliveryUpdatesResponse>(`/delivery/work-order/${workOrderId}/delivery-updates`);
    const updates = response.data?.updates || [];
    const singleUpdate = updates.find((update) => update.updateId === updateId);

    if (!singleUpdate) {
      return {
        error: {
          error: {
            message: 'Delivery update not found',
            code: 'DELIVERY_UPDATE_NOT_FOUND',
            statusCode: 404,
          }
        } as GenericErrorResponse,
      };
    }

    return { data: singleUpdate };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

export const createDeliveryUpdate = async (workOrderId: string, data: DeliveryUpdateCreateRequest): Promise<{ data?: unknown; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.post<unknown>(`/delivery/work-order/${workOrderId}/delivery-updates`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

export const updateDeliveryUpdate = async (workOrderId: string, updateId: string, data: DeliveryUpdateUpdateRequest): Promise<{ data?: unknown; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.patch<unknown>(`/delivery/work-order/${workOrderId}/delivery-updates/${updateId}`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

export const emailDeliveryUpdates = async (
  workOrderId: string,
  data: DeliveryUpdateEmailRequest
): Promise<{ data?: unknown; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.post<DeliveryUpdateEmailResponse>(`/delivery/work-order/${workOrderId}/delivery-updates/email`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

// Get images for a specific delivery update from the full updates list
export const getDeliveryUpdateImages = async (
  workOrderId: string,
  updateId: string
): Promise<{ data?: DeliveryUpdateImage[]; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.get<DeliveryUpdatesResponse>(`/delivery/work-order/${workOrderId}/delivery-updates`);
    const updates = response.data?.updates || [];
    const update = updates.find((u) => u.updateId === updateId);

    if (!update) {
      return {
        error: {
          error: {
            message: 'Delivery update not found',
            code: 'DELIVERY_UPDATE_NOT_FOUND',
            statusCode: 404,
          }
        } as GenericErrorResponse,
      };
    }

    // Resolve presigned URLs — direct S3 paths are not publicly accessible
    const images: DeliveryUpdateImage[] = [];
    for (const mediaWrapper of update.media ?? []) {
      const mediaId = mediaWrapper.media.mediaId;
      const imageUrl = await getMediaPresignedUrl(mediaId);
      if (imageUrl) {
        images.push({
          mediaId,
          imageUrl,
          mimeType: mediaWrapper.media.mimeType,
        });
      }
    }

    return { data: images };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
}

// React Query mutation hook for sending delivery update email
export const useSendDeliveryUpdateEmail = () => {
  return useMutation({
    mutationFn: async ({ workOrderId, imageIds }: { workOrderId: string; imageIds: string[] }) => {
      const result = await emailDeliveryUpdates(workOrderId, { imageIds });
      if (result.error) throw result.error;
      return result.data!;
    },
  });
}

// ========== INVOICE OPERATIONS ==========

/**
 * Get delivery invoice details by invoice number or UUID
 */
export const useGetDeliveryInvoice = (
  invoiceNumber: string | undefined,
  refreshKey?: number,
  options?: Omit<UseQueryOptions<DeliveryInvoiceDetailResponse>, 'queryKey' | 'queryFn' | 'enabled'>
) => {
  return useQuery<DeliveryInvoiceDetailResponse>({
    queryKey: ['deliveryInvoice', invoiceNumber, refreshKey],
    queryFn: async () => {
      if (!invoiceNumber) throw new Error('Invoice number is required');

      const response = await axiosInstance.get<DeliveryInvoiceDetailResponse>(
        `/delivery/invoices/${invoiceNumber}`
      );
      return response.data;
    },
    enabled: !!invoiceNumber,
    ...options,
  });
};

/**
 * Update delivery invoice - add, update, or remove billing items
 */
export const useUpdateDeliveryInvoiceMutation = () => {
  return useMutation<
    DeliveryInvoiceEditResponse,
    GenericErrorResponse,
    { invoiceId: string; data: DeliveryInvoiceEditRequest }
  >({
    mutationFn: async ({ invoiceId, data }) => {
      const response = await axiosInstance.put<DeliveryInvoiceEditResponse>(
        `/delivery/invoices/${invoiceId}`,
        data
      );
      return response.data;
    },
  });
};

/**
 * Legacy update invoice method (kept for backward compatibility)
 */
export const updateInvoice = async (
  invoiceId: string,
  data: unknown
): Promise<{ data?: unknown; error?: GenericErrorResponse }> => {
  try {
    const response = await axiosInstance.put<unknown>(`/delivery/invoices/${invoiceId}`, data);
    return { data: response.data };
  } catch (error) {
    return { error: error as GenericErrorResponse };
  }
};

/**
 * Download delivery invoice PDF
 */
let deliveryInvoicePdfDownloadInFlight = false;

export const downloadDeliveryInvoicePDF = async (invoiceNumber: string | null | undefined) => {
  if (deliveryInvoicePdfDownloadInFlight) return;

  try {
    if (!invoiceNumber) throw new Error('Invoice number is required');
    deliveryInvoicePdfDownloadInFlight = true;
    toast({
      description: 'Download will start shortly',
      duration: 3000,
    });
    const response = await axiosInstance.get<Blob>(`/delivery/invoices/${invoiceNumber}/pdf`, {
      responseType: 'blob',
    });
    downloadFilefromBuffer(response.data, `${invoiceNumber}.pdf`, 'PDF');
  } catch (err) {
    toast({
      description: 'Unable to generate PDF, please try again',
      duration: 3000,
      variant: 'destructive',
    });
  } finally {
    deliveryInvoicePdfDownloadInFlight = false;
  }
};

export const useSendDeliveryInvoiceMail = () => {
  return useMutation<{ message: string }, AxiosError<GenericErrorResponse>, { invoiceNumber: string }>({
    mutationFn: async ({ invoiceNumber }) => {
      const response = await axiosInstance.post<{ message: string }>(
        `/delivery/invoices/${invoiceNumber}/email`
      );
      return response.data;
    },
  });
};


export const deliveryService = {
  createDeliveryIntake,
  getDeliveryIntake,
  updateDeliveryIntake,
  getCounties,
  getDispatchRate,
  searchDgrUsers,
  getUserDetails,
  getClientDetails,
  getContactDetails,
  getDeliveryList,
  convertToWorkOrder,
  assignToMe,
  assignIntakeToMe,
  cancelWorkOrder,
  cancelIntakeForm,
  updateWorkOrder,
  rescheduleWorkOrder,
  updateWorkOrderStatus,
  exportDeliverySheet,
  getWorkOrderComments,
  addWorkOrderComment,
  getDeliveryUpdates,
  getSingleDeliveryUpdate,
  createDeliveryUpdate,
  updateDeliveryUpdate,
  emailDeliveryUpdates,
  getDeliveryUpdateImages,
  updateInvoice,
  downloadDeliveryInvoicePDF,
  getDeliveryDetail
};
